import React from 'react';
import {FadeIn} from '../ui/FadeIn';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiSparkles,FiYoutube,FiLayout,FiVideo,FiArrowDown,FiPlay}=FiIcons;

const Hero=()=> {
return (
    <section className="relative pt-32 pb-32 overflow-hidden">
      {/* Enhanced background effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-brand-electric/20 rounded-full blur-[200px] pointer-events-none animate-pulse"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <FadeIn delay={0.1}>
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-electric/60 bg-brand-electric/10 text-brand-electric text-sm font-bold mb-8 shadow-[0_0_30px_rgba(0,229,255,0.3)] backdrop-blur-xl">
            <SafeIcon icon={FiSparkles} className="text-lg icon-glow text-brand-electric" />
            <span className="drop-shadow-[0_0_5px_rgba(0,229,255,0.8)] text-brand-electric">AI-Powered Video Studio</span>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-[1.1]">
            <span className="block text-white drop-shadow-[0_2px_20px_rgba(255,255,255,0.3)]">Professional video</span>
            <span className="block text-white drop-shadow-[0_2px_20px_rgba(255,255,255,0.3)]">transformation.</span>
            <span className="block text-brand-neon drop-shadow-[0_2px_40px_rgba(124,58,237,0.5)] mt-2">Powered by AI.</span>
          </h1>
        </FadeIn>

        <FadeIn delay={0.3}>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-4 leading-relaxed font-medium drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
            Transform YouTube videos into PowerPoint decks. Turn presentations into videos. Rebrand content with AI voice cloning.
          </p>
          <p className="text-xl md:text-2xl text-brand-electric font-bold mb-12 drop-shadow-[0_0_10px_rgba(0,229,255,0.8)]">
            One studio. Infinite possibilities.
          </p>
        </FadeIn>

        <FadeIn delay={0.4} className="flex flex-col items-center justify-center gap-4 mb-14">
          <button className="btn-primary w-full sm:w-auto text-lg font-bold text-white px-10 py-4 flex items-center gap-3 justify-center">
            <SafeIcon icon={FiPlay} className="text-xl" />
            Watch Demo
          </button>
          <button className="btn-cta w-full sm:w-auto text-lg text-brand-neon">
            Start Free
          </button>
          <div className="text-sm font-semibold text-white/70">
            No Credit Card Required
          </div>
        </FadeIn>

        <FadeIn delay={0.5} className="flex flex-col items-center justify-center mb-20">
          <p className="text-base text-white/80 mb-4 font-semibold drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">Trusted by 1,200+ creators and teams worldwide</p>
          <div className="flex -space-x-3">
            {[1,2,3,4,5].map((i)=> (
              <div key={i} className="w-12 h-12 rounded-full border-3 border-brand-dark bg-brand-border overflow-hidden ring-2 ring-brand-electric/50 shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:scale-110 transition-transform">
                <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </FadeIn>

        {/* Hero Visual Mockup */}
        <FadeIn delay={0.6} className="relative max-w-6xl mx-auto">
          {/* Enhanced glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-neon/30 via-brand-electric/30 to-brand-mint/30 blur-[150px] rounded-full"></div>

          <div className="relative studio-card p-8 md:p-12 border border-white/10">
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8 md:gap-10">
              {/* Box 1 - Input */}
              <div className="glass-card p-8 w-full md:w-1/3 flex flex-col items-center border border-white/10 hover:border-brand-electric/40 hover:shadow-[0_0_40px_rgba(0,229,255,0.4)] transition-all duration-500">
                <SafeIcon icon={FiYoutube} className="text-red-500 text-5xl mb-6 icon-glow" />
                <div className="w-full bg-brand-darker/90 rounded-xl px-6 py-4 text-sm font-mono text-white border border-white/10 truncate shadow-[inset_0_0_20px_rgba(0,229,255,0.1)]">
                  youtube.com/watch?v=...
                </div>
              </div>

              {/* Arrow 1 */}
              <div className="flex flex-col items-center">
                <span className="text-xs uppercase font-black tracking-widest mb-2 hidden md:block text-brand-electric bg-brand-electric/20 px-4 py-2 rounded-full border border-brand-electric/40 shadow-[0_0_20px_rgba(0,229,255,0.3)]">
                  AI Analysis
                </span>
                <SafeIcon icon={FiArrowDown} className="md:hidden text-3xl my-2 text-brand-electric icon-glow rotate-0 md:rotate-90" />
                <div className="hidden md:block text-3xl text-brand-electric icon-glow">→</div>
              </div>

              {/* Box 2 - Processing (Highlighted) */}
              <div className="glass-card p-8 w-full md:w-1/3 relative border border-brand-neon/60 shadow-[0_0_50px_rgba(124,58,237,0.5)] hover:shadow-[0_0_70px_rgba(124,58,237,0.7)] transition-all duration-500">
                {/* Animated pulse indicators */}
                <div className="absolute top-4 right-4 flex gap-3">
                  <div className="w-4 h-4 rounded-full bg-brand-electric animate-pulse shadow-[0_0_15px_rgba(0,229,255,0.8)]"></div>
                  <div className="w-4 h-4 rounded-full bg-brand-neon animate-pulse shadow-[0_0_15px_rgba(124,58,237,0.8)] animation-delay-150"></div>
                </div>
                <SafeIcon icon={FiLayout} className="text-brand-neon text-5xl mb-6 mx-auto icon-glow" />
                <div className="grid grid-cols-2 gap-4">
                  {[1,2,3,4].map(i=> (
                    <div key={i} className="bg-brand-darker/50 h-12 rounded-lg border border-brand-neon/40 shadow-[inset_0_0_15px_rgba(124,58,237,0.2)]"></div>
                  ))}
                </div>
              </div>

              {/* Arrow 2 */}
              <div className="flex flex-col items-center">
                <span className="text-xs uppercase font-black tracking-widest mb-2 hidden md:block text-brand-mint bg-brand-mint/20 px-4 py-2 rounded-full border border-brand-mint/40 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                  Voice Synthesis
                </span>
                <SafeIcon icon={FiArrowDown} className="md:hidden text-3xl my-2 text-brand-mint icon-glow rotate-0 md:rotate-90" />
                <div className="hidden md:block text-3xl text-brand-mint icon-glow">→</div>
              </div>

              {/* Box 3 - Output */}
              <div className="glass-card p-8 w-full md:w-1/3 border border-white/10 hover:border-brand-electric/40 hover:shadow-[0_0_40px_rgba(0,229,255,0.4)] transition-all duration-500">
                <div className="aspect-video bg-brand-darker/90 rounded-xl border border-white/10 overflow-hidden relative flex items-center justify-center shadow-[inset_0_0_30px_rgba(0,229,255,0.1)]">
                  <SafeIcon icon={FiVideo} className="text-brand-electric text-4xl icon-glow" />
                  <div className="absolute bottom-0 left-0 right-0 h-2 bg-brand-darker/80 border-t border-brand-electric/20">
                    <div className="h-full bg-gradient-to-r from-brand-electric to-brand-neon w-1/3 shadow-[0_0_15px_rgba(0,229,255,0.6)]"></div>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-studio-gradient shadow-[0_0_15px_rgba(0,229,255,0.6)] animate-pulse"></div>
                  <div className="flex-1 h-2 bg-brand-darker/80 rounded-full overflow-hidden flex gap-1 border border-brand-electric/20">
                    {[...Array(20)].map((_,i)=> (
                      <div key={i} className="w-full bg-gradient-to-t from-brand-electric/70 to-brand-neon/50 shadow-[0_0_5px_rgba(0,229,255,0.4)]" style={{height: `${Math.random() * 100}%`}}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default Hero;